/**
 * Единый источник правды: почему регистрация в RealColibri идёт через
 * TronLink-кошелёк, а не через e-mail/пароль.  Контент про конфиденциальность
 * и безопасность депозитов используется в ДВУХ местах с одним текстом:
 *   - компонент <WhyTronLink/> (форма регистрации и др.);
 *   - гайд «Why register with TronLink» (раздел Guides).
 * 7 языков; при отсутствии перевода — фолбэк на en.
 */

export interface WhyTronLinkPoint {
    title: string
    text: string
}

export interface WhyTronLinkContent {
    title: string
    intro: string
    points: WhyTronLinkPoint[]
    note: string
}

export const whyTronLinkContent: Record<string, WhyTronLinkContent> = {
    en: {
        title: 'Why register with TronLink?',
        intro:
            'RealColibri signs you in with your TronLink wallet instead of an email and password. This is a deliberate choice to protect your privacy and your money — and there is nothing to fear.',
        points: [
            {
                title: 'No personal data to steal',
                text: 'No email, phone, name or password. We do not collect personal information, so there is nothing for attackers to leak, sell or hand over. Your identity stays private.',
            },
            {
                title: 'You alone control your wallet',
                text: 'TronLink is non-custodial — only you hold the private keys. RealColibri can never access, move or freeze your funds. Connecting simply lets you sign in and pay the algorithm fee in RCC.',
            },
            {
                title: 'Your deposits stay yours',
                text: 'Your trading capital remains on your own exchange accounts. The robot connects through trade-only API keys and can never withdraw your money. Your TronLink wallet holds only the RCC used for fees.',
            },
            {
                title: 'A login that cannot be phished',
                text: 'You sign in by cryptographically signing a message with your wallet. There is no password to guess, reuse or leak in a data breach.',
            },
            {
                title: 'Trusted, not unknown',
                text: 'TronLink is an audited wallet used by millions. It only asks you to approve a connection and sign a request — it never hands control of your funds to anyone, including us.',
            },
        ],
        note: 'Blockchain network (gas) fees are charged by the Tron network, not by RealColibri.',
    },
    ru: {
        title: 'Почему регистрация через TronLink?',
        intro:
            'RealColibri выполняет вход через ваш кошелёк TronLink, а не через e-mail и пароль. Это осознанный выбор ради защиты вашей конфиденциальности и ваших средств — и бояться тут нечего.',
        points: [
            {
                title: 'Нечего красть из персональных данных',
                text: 'Ни почты, ни телефона, ни имени, ни пароля. Мы не собираем персональные данные — значит, нечего слить, продать или передать. Ваша личность остаётся приватной.',
            },
            {
                title: 'Кошельком управляете только вы',
                text: 'TronLink не хранит ваши средства у себя — приватные ключи есть только у вас. RealColibri не может получить доступ к вашим средствам, перевести их или заморозить. Подключение нужно лишь для входа и оплаты комиссии за алгоритм в RCC.',
            },
            {
                title: 'Ваши депозиты остаются вашими',
                text: 'Торговый капитал остаётся на ваших биржевых счетах. Робот подключается через API-ключи только на торговлю и не может вывести ваши деньги. В кошельке TronLink хранится лишь RCC для оплаты комиссии.',
            },
            {
                title: 'Вход, который нельзя выманить',
                text: 'Вы входите, криптографически подписывая сообщение кошельком. Нет пароля, который можно подобрать, переиспользовать или украсть при утечке.',
            },
            {
                title: 'Проверено, а не неизвестно',
                text: 'TronLink — проверенный кошелёк, которым пользуются миллионы. Он лишь просит подтвердить подключение и подписать запрос — и никогда не передаёт контроль над вашими средствами никому, включая нас.',
            },
        ],
        note: 'Комиссия сети (gas) взимается блокчейном Tron, а не RealColibri.',
    },
    es: {
        title: '¿Por qué registrarse con TronLink?',
        intro:
            'RealColibri inicia tu sesión con tu billetera TronLink en lugar de un correo y contraseña. Es una decisión deliberada para proteger tu privacidad y tu dinero, y no hay nada que temer.',
        points: [
            {
                title: 'Sin datos personales que robar',
                text: 'Sin correo, teléfono, nombre ni contraseña. No recopilamos información personal, así que no hay nada que filtrar, vender o entregar. Tu identidad permanece privada.',
            },
            {
                title: 'Solo tú controlas tu billetera',
                text: 'TronLink no es custodial: solo tú tienes las claves privadas. RealColibri nunca puede acceder, mover ni congelar tus fondos. Conectarte solo permite iniciar sesión y pagar la comisión del algoritmo en RCC.',
            },
            {
                title: 'Tus depósitos siguen siendo tuyos',
                text: 'Tu capital de trading permanece en tus propias cuentas de exchange. El robot se conecta mediante claves API solo de trading y nunca puede retirar tu dinero. Tu billetera TronLink solo guarda los RCC para las comisiones.',
            },
            {
                title: 'Un inicio de sesión imposible de phishing',
                text: 'Inicias sesión firmando criptográficamente un mensaje con tu billetera. No hay contraseña que adivinar, reutilizar o filtrar en una brecha de datos.',
            },
            {
                title: 'De confianza, no desconocido',
                text: 'TronLink es una billetera auditada usada por millones. Solo te pide aprobar una conexión y firmar una solicitud; nunca entrega el control de tus fondos a nadie, incluidos nosotros.',
            },
        ],
        note: 'Las comisiones de red (gas) las cobra la red Tron, no RealColibri.',
    },
    hy: {
        title: 'Ինչու՞ գրանցվել TronLink-ով',
        intro:
            'RealColibri-ն մուտքն իրականացնում է ձեր TronLink դրամապանակով՝ էլ. փոստի և գաղտնաբառի փոխարեն։ Սա գիտակցված ընտրություն է՝ ձեր գաղտնիությունն ու միջոցները պաշտպանելու համար, և վախենալու ոչինչ չկա։',
        points: [
            {
                title: 'Գողանալու անձնական տվյալներ չկան',
                text: 'Ոչ էլ. փոստ, ոչ հեռախոս, ոչ անուն, ոչ գաղտնաբառ։ Մենք անձնական տվյալներ չենք հավաքում, ուստի արտահոսելու կամ վաճառելու բան չկա։ Ձեր ինքնությունը մնում է գաղտնի։',
            },
            {
                title: 'Դրամապանակը կառավարում եք միայն դուք',
                text: 'TronLink-ը ոչ-պահառուական է. մասնավոր բանալիները միայն ձեզ մոտ են։ RealColibri-ն երբեք չի կարող մուտք գործել, տեղափոխել կամ սառեցնել ձեր միջոցները։ Միացումը միայն թույլ է տալիս մուտք գործել և RCC-ով վճարել ալգորիթմի վճարը։',
            },
            {
                title: 'Ձեր ավանդները մնում են ձերը',
                text: 'Ձեր առևտրային կապիտալը մնում է ձեր բորսայական հաշիվներում։ Ռոբոտը միանում է միայն առևտրի API բանալիներով և երբեք չի կարող դուրս բերել ձեր գումարը։ Ձեր TronLink դրամապանակում պահվում է միայն վճարների համար նախատեսված RCC-ն։',
            },
            {
                title: 'Մուտք, որը հնարավոր չէ խաբեությամբ կորզել',
                text: 'Դուք մուտք եք գործում՝ ծածկագրորեն ստորագրելով հաղորդագրությունը ձեր դրամապանակով։ Չկա գաղտնաբառ, որը կարելի է գուշակել, կրկնակի օգտագործել կամ արտահոսքով գողանալ։',
            },
            {
                title: 'Վստահելի, ոչ թե անծանոթ',
                text: 'TronLink-ը աուդիտ անցած դրամապանակ է, որն օգտագործում են միլիոնավոր մարդիկ։ Այն միայն խնդրում է հաստատել միացումը և ստորագրել հարցումը. երբեք չի փոխանցում ձեր միջոցների վերահսկողությունը որևէ մեկին, ներառյալ մեզ։',
            },
        ],
        note: 'Ցանցի (gas) վճարը գանձում է Tron բլոկչեյնը, ոչ թե RealColibri-ն։',
    },
    fr: {
        title: 'Pourquoi s’inscrire avec TronLink ?',
        intro:
            'RealColibri vous connecte avec votre portefeuille TronLink plutôt qu’avec un e-mail et un mot de passe. C’est un choix délibéré pour protéger votre vie privée et votre argent — et il n’y a rien à craindre.',
        points: [
            {
                title: 'Aucune donnée personnelle à voler',
                text: 'Ni e-mail, ni téléphone, ni nom, ni mot de passe. Nous ne collectons pas d’informations personnelles : il n’y a donc rien à divulguer, vendre ou transmettre. Votre identité reste privée.',
            },
            {
                title: 'Vous seul contrôlez votre portefeuille',
                text: 'TronLink est non-dépositaire : vous seul détenez les clés privées. RealColibri ne peut jamais accéder à vos fonds, les déplacer ni les geler. La connexion sert uniquement à vous identifier et à payer les frais de l’algorithme en RCC.',
            },
            {
                title: 'Vos dépôts restent les vôtres',
                text: 'Votre capital de trading reste sur vos propres comptes d’échange. Le robot se connecte via des clés API limitées au trading et ne peut jamais retirer votre argent. Votre portefeuille TronLink ne contient que les RCC servant aux frais.',
            },
            {
                title: 'Une connexion impossible à hameçonner',
                text: 'Vous vous connectez en signant cryptographiquement un message avec votre portefeuille. Aucun mot de passe à deviner, réutiliser ou divulguer lors d’une fuite de données.',
            },
            {
                title: 'De confiance, pas inconnu',
                text: 'TronLink est un portefeuille audité utilisé par des millions de personnes. Il ne fait que vous demander d’approuver une connexion et de signer une requête ; il ne confie jamais le contrôle de vos fonds à qui que ce soit, nous y compris.',
            },
        ],
        note: 'Les frais de réseau (gas) sont prélevés par le réseau Tron, et non par RealColibri.',
    },
    de: {
        title: 'Warum mit TronLink registrieren?',
        intro:
            'RealColibri meldet Sie mit Ihrer TronLink-Wallet an, statt mit E-Mail und Passwort. Das ist eine bewusste Entscheidung zum Schutz Ihrer Privatsphäre und Ihres Geldes — und es gibt nichts zu befürchten.',
        points: [
            {
                title: 'Keine persönlichen Daten zum Stehlen',
                text: 'Keine E-Mail, kein Telefon, kein Name, kein Passwort. Wir erheben keine persönlichen Daten, also gibt es nichts, was geleakt, verkauft oder herausgegeben werden könnte. Ihre Identität bleibt privat.',
            },
            {
                title: 'Nur Sie kontrollieren Ihre Wallet',
                text: 'TronLink ist nicht-verwahrend — nur Sie besitzen die privaten Schlüssel. RealColibri kann niemals auf Ihre Gelder zugreifen, sie bewegen oder einfrieren. Die Verbindung dient nur der Anmeldung und der Zahlung der Algorithmus-Gebühr in RCC.',
            },
            {
                title: 'Ihre Einlagen bleiben Ihre',
                text: 'Ihr Handelskapital verbleibt auf Ihren eigenen Börsenkonten. Der Roboter verbindet sich über reine Trading-API-Schlüssel und kann Ihr Geld niemals abheben. Ihre TronLink-Wallet hält nur die RCC für die Gebühren.',
            },
            {
                title: 'Ein Login, das nicht gephisht werden kann',
                text: 'Sie melden sich an, indem Sie eine Nachricht kryptografisch mit Ihrer Wallet signieren. Es gibt kein Passwort, das erraten, wiederverwendet oder bei einem Datenleck gestohlen werden kann.',
            },
            {
                title: 'Vertrauenswürdig, nicht unbekannt',
                text: 'TronLink ist eine geprüfte Wallet, die von Millionen genutzt wird. Sie bittet Sie nur, eine Verbindung zu bestätigen und eine Anfrage zu signieren — sie übergibt die Kontrolle über Ihre Gelder niemandem, auch uns nicht.',
            },
        ],
        note: 'Netzwerkgebühren (Gas) werden vom Tron-Netzwerk erhoben, nicht von RealColibri.',
    },
    it: {
        title: 'Perché registrarsi con TronLink?',
        intro:
            'RealColibri ti fa accedere con il tuo wallet TronLink anziché con email e password. È una scelta consapevole per proteggere la tua privacy e il tuo denaro — e non c’è nulla da temere.',
        points: [
            {
                title: 'Nessun dato personale da rubare',
                text: 'Niente email, telefono, nome o password. Non raccogliamo informazioni personali, quindi non c’è nulla da divulgare, vendere o consegnare. La tua identità resta privata.',
            },
            {
                title: 'Solo tu controlli il tuo wallet',
                text: 'TronLink è non-custodial: solo tu possiedi le chiavi private. RealColibri non può mai accedere, spostare o congelare i tuoi fondi. La connessione serve solo per accedere e pagare la commissione dell’algoritmo in RCC.',
            },
            {
                title: 'I tuoi depositi restano tuoi',
                text: 'Il tuo capitale di trading rimane sui tuoi conti di exchange. Il robot si connette tramite chiavi API solo per il trading e non può mai prelevare il tuo denaro. Il tuo wallet TronLink contiene solo gli RCC per le commissioni.',
            },
            {
                title: 'Un accesso che non può essere phishato',
                text: 'Accedi firmando crittograficamente un messaggio con il tuo wallet. Non c’è alcuna password da indovinare, riutilizzare o trafugare in una violazione di dati.',
            },
            {
                title: 'Affidabile, non sconosciuto',
                text: 'TronLink è un wallet verificato usato da milioni di persone. Ti chiede solo di approvare una connessione e firmare una richiesta; non cede mai il controllo dei tuoi fondi a nessuno, noi compresi.',
            },
        ],
        note: 'Le commissioni di rete (gas) sono addebitate dalla rete Tron, non da RealColibri.',
    },
}
